import { showBackofficeFlash } from "packs/init-backoffice-flashes"

const decisionModal = () => document.querySelector('[data-pending-modal="decision"]')
const paymentModal = () => document.querySelector('[data-pending-modal="payment"]')
const paymentDeleteModal = () => document.querySelector('[data-pending-modal="payment-delete"]')

const csrfToken = (root) =>
  root?.dataset.csrfToken ||
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const openModal = (modal) => {
  if (!modal) return
  modal.hidden = false
  document.body.style.overflow = "hidden"
}

const closeModal = (modal) => {
  if (!modal) return
  modal.hidden = true
  if (![...document.querySelectorAll(".backoffice-modal")].some((el) => !el.hidden)) {
    document.body.style.overflow = ""
  }
}

const closeAllPendingModals = () => {
  closeModal(decisionModal())
  closeModal(paymentModal())
  closeModal(paymentDeleteModal())
}

const showFlash = (message, kind = "notice") => {
  showBackofficeFlash(message, kind === "alert" ? "alert" : "notice")
}

const postPendingAction = async ({ url, body, root, method = "POST" }) => {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(root),
      "X-Requested-With": "XMLHttpRequest"
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: "same-origin"
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data.ok) {
    throw new Error(data.error || "Une erreur est survenue.")
  }
  return data
}

const pendingRoot = () => document.querySelector("[data-pending-actions-root]")

const handlePendingSuccess = (data, modal) => {
  closeModal(modal)
  showFlash(data.notice, "notice")
  window.Turbolinks ? Turbolinks.visit(window.location.href) : window.location.reload()
}

const bindDecisionModal = (_root) => {
  const modal = decisionModal()
  if (!modal || modal.dataset.bound === "true") return
  modal.dataset.bound = "true"

  modal.querySelector("[data-pending-decision-submit]")?.addEventListener("click", async () => {
    const decision = modal.dataset.decision
    const exhibitorId = modal.dataset.exhibitorId
    const participantType = modal.dataset.participantType || "exhibitor"
    if (!decision || !exhibitorId) return
    const root = document.querySelector("[data-pending-actions-root]")

    const note = modal.querySelector("[data-pending-decision-note]")?.value || ""
    const url =
      decision === "approve"
        ? `/back-office/actions-en-attente/${exhibitorId}/approve`
        : `/back-office/actions-en-attente/${exhibitorId}/reject`

    const submit = modal.querySelector("[data-pending-decision-submit]")
    submit.disabled = true
    try {
      const data = await postPendingAction({
        url,
        body: { note, participant_type: participantType },
        root
      })
      handlePendingSuccess(data, modal)
    } catch (error) {
      showFlash(error.message, "alert")
    } finally {
      submit.disabled = false
    }
  })
}

const bindPaymentModal = (_root) => {
  const modal = paymentModal()
  if (!modal || modal.dataset.bound === "true") return
  modal.dataset.bound = "true"

  modal.querySelector("[data-pending-payment-submit]")?.addEventListener("click", async () => {
    const exhibitorId = modal.dataset.exhibitorId
    const participantType = modal.dataset.participantType || "exhibitor"
    if (!exhibitorId) return
    const root = pendingRoot()

    const amount = modal.querySelector("[data-pending-payment-amount]")?.value || ""
    const paidOn = modal.querySelector("[data-pending-payment-paid-on]")?.value || ""
    const internalNote = modal.querySelector("[data-pending-payment-internal-note]")?.value || ""
    const commands = modal.querySelector("[data-pending-payment-commands]")?.value || ""
    const paymentMethod =
      modal.querySelector('[data-pending-payment-method]:checked')?.value ||
      ""
    const mode = modal.dataset.mode || "create"
    const paymentId = modal.dataset.paymentId

    const submit = modal.querySelector("[data-pending-payment-submit]")
    submit.disabled = true
    try {
      const body = {
        amount,
        payment_method: paymentMethod,
        paid_on: paidOn,
        internal_note: internalNote,
        commands,
        participant_type: participantType
      }
      const data =
        mode === "edit" && paymentId
          ? await postPendingAction({
              url: `/back-office/paiements/${paymentId}`,
              method: "PATCH",
              body,
              root
            })
          : await postPendingAction({
              url: `/back-office/actions-en-attente/${exhibitorId}/record_payment`,
              body,
              root
            })
      handlePendingSuccess(data, modal)
    } catch (error) {
      showFlash(error.message, "alert")
    } finally {
      submit.disabled = false
    }
  })
}

const bindPaymentDeleteModal = (_root) => {
  const modal = paymentDeleteModal()
  if (!modal || modal.dataset.bound === "true") return
  modal.dataset.bound = "true"

  modal.querySelector("[data-pending-payment-delete-submit]")?.addEventListener("click", async () => {
    const paymentId = modal.dataset.paymentId
    if (!paymentId) return
    const root = pendingRoot()
    const submit = modal.querySelector("[data-pending-payment-delete-submit]")
    submit.disabled = true
    try {
      const data = await postPendingAction({
        url: `/back-office/paiements/${paymentId}`,
        method: "DELETE",
        body: {},
        root
      })
      handlePendingSuccess(data, modal)
    } catch (error) {
      showFlash(error.message, "alert")
    } finally {
      submit.disabled = false
    }
  })
}

const openDecision = ({ decision, exhibitorId, company, participantType = "exhibitor" }) => {
  const modal = decisionModal()
  if (!modal) return
  modal.dataset.decision = decision
  modal.dataset.exhibitorId = String(exhibitorId)
  modal.dataset.participantType = participantType

  const title = modal.querySelector("[data-pending-decision-title]")
  const subtitle = modal.querySelector("[data-pending-decision-subtitle]")
  const submit = modal.querySelector("[data-pending-decision-submit]")
  const note = modal.querySelector("[data-pending-decision-note]")

  if (decision === "approve") {
    if (title) title.textContent = "Valider l’inscription"
    if (submit) {
      submit.textContent = "Valider"
      submit.classList.remove("backoffice-btn--danger")
      submit.classList.add("backoffice-btn--primary")
    }
  } else {
    if (title) title.textContent = "Rejeter l’inscription"
    if (submit) {
      submit.textContent = "Rejeter"
      submit.classList.add("backoffice-btn--danger")
      submit.classList.remove("backoffice-btn--primary")
    }
  }
  if (subtitle) subtitle.textContent = company || ""
  if (note) note.value = ""
  openModal(modal)
  note?.focus()
}

const todayIsoDate = () => {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${month}-${day}`
}

const formatPaidOnDisplay = (iso) => {
  if (!iso) return ""
  const [year, month, day] = iso.split("-")
  if (!year || !month || !day) return iso
  return `${day}/${month}/${year}`
}

const formatAmountFr = (raw) => {
  if (raw == null || raw === "") return ""
  const normalized = String(raw).trim().replace(/\s/g, "").replace(",", ".")
  const n = Number(normalized)
  if (Number.isNaN(n)) return String(raw).replace(/\./g, ",")
  return n.toFixed(2).replace(".", ",")
}

const openPayment = ({
  mode = "create",
  paymentId = null,
  exhibitorId,
  company,
  expectedAmount,
  paymentMethod = "bank_transfer",
  paidOn = null,
  internalNote = "",
  commands = "",
  participantType = "exhibitor"
}) => {
  const modal = paymentModal()
  if (!modal) return
  modal.dataset.mode = mode
  modal.dataset.exhibitorId = String(exhibitorId)
  modal.dataset.participantType = participantType
  modal.dataset.paymentId = paymentId ? String(paymentId) : ""

  const title = modal.querySelector("#pending-payment-title")
  const amount = modal.querySelector("[data-pending-payment-amount]")
  const paidOnInput = modal.querySelector("[data-pending-payment-paid-on]")
  const noteInput = modal.querySelector("[data-pending-payment-internal-note]")
  const commandsInput = modal.querySelector("[data-pending-payment-commands]")
  const submit = modal.querySelector("[data-pending-payment-submit]")
  const selectedMethod = paymentMethod === "check" ? "check" : "bank_transfer"

  if (title) {
    title.textContent = mode === "edit" ? "Modifier le paiement" : "Enregistrer un paiement"
  }
  if (submit) {
    submit.textContent = "Enregistrer"
  }
  if (amount) {
    amount.value = formatAmountFr(expectedAmount)
  }
  if (paidOnInput) {
    paidOnInput.value = paidOn || todayIsoDate()
  }
  if (noteInput) {
    noteInput.value = internalNote || ""
  }
  if (commandsInput) {
    commandsInput.value = commands || ""
  }

  modal.querySelectorAll("[data-pending-payment-method]").forEach((input) => {
    input.checked = input.value === selectedMethod
  })

  openModal(modal)
  amount?.focus()
}

const openPaymentDelete = ({ paymentId, amount, paymentMethod, paidOn }) => {
  const modal = paymentDeleteModal()
  if (!modal) return
  modal.dataset.paymentId = String(paymentId)

  const summary = modal.querySelector("[data-pending-payment-delete-summary]")
  if (summary) {
    const parts = []
    const amountLabel = formatAmountFr(amount)
    if (amountLabel) parts.push(`${amountLabel} €`)
    if (paymentMethod) parts.push(paymentMethod)
    if (paidOn) parts.push(formatPaidOnDisplay(paidOn))
    summary.textContent = parts.join(" · ")
  }

  openModal(modal)
  modal.querySelector("[data-pending-payment-delete-submit]")?.focus()
}

const onClick = (event) => {
  const trigger = event.target.closest("[data-pending-open]")
  if (!trigger) return

  const root =
    trigger.closest("[data-pending-actions-root]") ||
    document.querySelector("[data-pending-actions-root]")
  if (!root || !root.contains(trigger)) return

  const kind = trigger.dataset.pendingOpen
  const exhibitorId = trigger.dataset.exhibitorId
  const participantType = trigger.dataset.participantType || "exhibitor"
  if (!kind || !exhibitorId) return

  if (kind === "approve" || kind === "reject") {
    openDecision({
      decision: kind,
      exhibitorId,
      company: trigger.dataset.company,
      participantType
    })
    return
  }

  if (kind === "payment") {
    openPayment({
      mode: "create",
      exhibitorId,
      company: trigger.dataset.company,
      expectedAmount: trigger.dataset.expectedAmount,
      paymentMethod: "bank_transfer",
      participantType
    })
    return
  }

  if (kind === "payment-edit") {
    openPayment({
      mode: "edit",
      paymentId: trigger.dataset.paymentId,
      exhibitorId,
      company: trigger.dataset.company,
      expectedAmount: trigger.dataset.amount,
      paymentMethod: trigger.dataset.paymentMethod,
      paidOn: trigger.dataset.paidOn,
      internalNote: trigger.dataset.internalNote || "",
      commands: trigger.dataset.commands || "",
      participantType
    })
    return
  }

  if (kind === "payment-delete") {
    openPaymentDelete({
      paymentId: trigger.dataset.paymentId,
      amount: trigger.dataset.amount,
      paymentMethod: trigger.dataset.paymentMethod,
      paidOn: trigger.dataset.paidOn
    })
  }
}

export const initBackofficePendingActions = () => {
  const root = document.querySelector("[data-pending-actions-root]")
  if (!root) return

  bindDecisionModal(root)
  bindPaymentModal(root)
  bindPaymentDeleteModal(root)

  if (document.documentElement.dataset.pendingActionsClickBound !== "true") {
    document.documentElement.dataset.pendingActionsClickBound = "true"
    document.addEventListener("click", onClick)
  }
}
